import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import FilterAlt from '@mui/icons-material/FilterAlt';
import ViewColumn from '@mui/icons-material/ViewColumn';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import type { ChipFilterValue } from '@openx/types/chipFilters';
import { useWindowSize } from '@openx/utils/hooks/useWindowSize';
import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';
import type { ListBulkAddFooterProps } from '../List/components/ListBulkAddFooter/ListBulkAddFooter';
import type { ListTopBarOption } from '../List/components/ListTopBar/ListTopBar';
import { ListChipFilters, type ListChipFiltersProps } from '../List/filters/ListChipFilters/ListChipFilters';
import {
  ListItemFieldsFilter,
  type ListItemFieldsFilterProps,
} from '../List/filters/ListItemFieldsFilter/ListItemFieldsFilter';
import { ArrowButton } from './components/ArrowButton';
import { TRANSFER_LIST_WITH_BULK_ADD_HEIGHT } from './constants';
import { useTransferListItemFields } from './hooks/useTransferListItemFields';
import { TransferListPanel, type TransferListPanelProps } from './TransferListPanel';
import type { TransferListBaseItem } from './types';

export type TransferListProps<
  TItem extends TransferListBaseItem,
  TFilters extends Record<string, ChipFilterValue> | undefined,
> = {
  ListItemComponent: TransferListPanelProps<TItem>['ListItemComponent'];
  error?: string;
  fields: {
    config: ListItemFieldsFilterProps<TItem>['fields'];
    localStorageKey: string;
  };
  isDisabled?: boolean;
  isLoading?: boolean;
  leftList: {
    chipFilters:
      | {
          config: ListChipFiltersProps<NonNullable<TFilters>>['filtersConfig'];
          onChange: ListChipFiltersProps<NonNullable<TFilters>>['onChange'];
          value: ListChipFiltersProps<NonNullable<TFilters>>['filters'];
        }
      | undefined;
    dataTest?: string;
    isItemBlocked?: TransferListPanelProps<TItem>['isItemBlocked'];
    isLoading?: boolean;
    isSortedDescending?: boolean;
    items: TransferListPanelProps<TItem>['items'];
    itemsSelected: TransferListPanelProps<TItem>['itemsSelected'];
    onSelectAll: TransferListPanelProps<TItem>['onSelectAll'];
    onSelectItem: TransferListPanelProps<TItem>['onSelectItem'];
    onTransfer: TransferListPanelProps<TItem>['onTransfer'];
    searchBarPlaceholder: TransferListPanelProps<TItem>['searchBarPlaceholder'];
    searchPhrase: TransferListPanelProps<TItem>['searchPhrase'];
    setIsSortedDescending?: Dispatch<SetStateAction<boolean>>;
    setSearchPhrase: TransferListPanelProps<TItem>['setSearchPhrase'];
    title: string;
    titleDataTest?: string;
  };
  onBulkAdd: ListBulkAddFooterProps<TItem>['onBulkAdd'];
  onBulkValidate: ListBulkAddFooterProps<TItem>['onValidate'];
  rightList: {
    dataTest?: string;
    items: TransferListPanelProps<TItem>['items'];
    itemsSelected: TransferListPanelProps<TItem>['itemsSelected'];
    onSearch: TransferListPanelProps<TItem>['onSearch'];
    onSelectAll: TransferListPanelProps<TItem>['onSelectAll'];
    onSelectItem: TransferListPanelProps<TItem>['onSelectItem'];
    onTransfer: TransferListPanelProps<TItem>['onTransfer'];
    searchBarPlaceholder: TransferListPanelProps<TItem>['searchBarPlaceholder'];
    title: string;
    titleDataTest?: string;
  };
};

export const TransferList = <TItem extends TransferListBaseItem, TFilters extends Record<string, ChipFilterValue>>({
  ListItemComponent,
  error,
  fields,
  isDisabled,
  isLoading,
  leftList,
  onBulkAdd,
  onBulkValidate,
  rightList,
}: TransferListProps<TItem, TFilters>) => {
  const [rightSearchPhrase, setRightSearchPhrase] = useState<string>('');
  const [fieldsFilterAnchor, setFieldsFilterAnchor] = useState<HTMLElement | null>(null);
  const [chipFiltersAnchor, setChipFiltersAnchor] = useState<HTMLElement | null>(null);
  const { fieldsConfig, handleFieldFilterClick, handleFieldFilterOrderChange } = useTransferListItemFields(fields);
  const { width: windowWidth } = useWindowSize();

  const setIsSortedDescending = leftList.setIsSortedDescending;

  const searchBarOptions = useMemo<ListTopBarOption[]>(() => {
    const manageInfoOption: ListTopBarOption = {
      Icon: ViewColumn,
      name: 'Manage Info*',
      onClick: anchor => setFieldsFilterAnchor(anchor),
    };

    const filtersOption: ListTopBarOption = {
      Icon: FilterAlt,
      hasBottomDivider: true,
      name: 'Filters',
      onClick: anchor => setChipFiltersAnchor(anchor),
    };

    const options: ListTopBarOption[] = leftList.chipFilters ? [filtersOption, manageInfoOption] : [manageInfoOption];

    if (setIsSortedDescending !== undefined) {
      return [
        {
          Icon: ArrowUpward,
          name: 'Sort ASC',
          onClick: () => setIsSortedDescending?.(false),
        },
        {
          Icon: ArrowDownward,
          hasBottomDivider: true,
          name: 'Sort DESC',
          onClick: () => setIsSortedDescending?.(true),
        },
        ...options,
      ];
    }

    return options;
  }, [leftList.chipFilters, setIsSortedDescending]);

  return (
    <Grid container columns={32} sx={{ flexGrow: 1, justifyContent: 'space-between' }}>
      <Grid size={15} sx={{ pr: 1 }}>
        <Typography data-test={leftList.titleDataTest} gutterBottom variant="h3">
          {leftList.title}
        </Typography>
        <TransferListPanel<TItem>
          ListItemComponent={ListItemComponent}
          dataTest={leftList.dataTest}
          checkboxDataTest="left-select-all"
          emptyListText="Start to type or apply filters in order to search for items."
          fields={fieldsConfig}
          headerButtonColor="primary"
          headerButtonText="Add Selected"
          isDisabled={isDisabled}
          isItemBlocked={leftList.isItemBlocked}
          isLoading={isLoading || leftList.isLoading}
          isSortedDescending={leftList.isSortedDescending}
          items={leftList.items}
          itemsSelected={leftList.itemsSelected}
          onSelectAll={leftList.onSelectAll}
          onSelectItem={leftList.onSelectItem}
          onTransfer={leftList.onTransfer}
          searchBarPlaceholder={leftList.searchBarPlaceholder}
          searchBarOptions={searchBarOptions}
          searchInputDataTest="left-search-input"
          searchPhrase={leftList.searchPhrase}
          setSearchPhrase={leftList.setSearchPhrase}
          windowWidth={windowWidth}
        />
      </Grid>
      <Grid
        size={0.75}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          gap: 1,
          justifyContent: 'center',
        }}
      >
        <ArrowButton
          dataTest="add-selected-arrow"
          disabled={isDisabled || isLoading || leftList.isLoading}
          Icon={ArrowForward}
          onTransfer={leftList.onTransfer}
          tooltipText="Move to Assigned List"
        />

        <ArrowButton
          dataTest="remove-selected-arrow"
          disabled={isDisabled || isLoading}
          Icon={ArrowBack}
          onTransfer={rightList.onTransfer}
          tooltipText="Move to List"
        />
      </Grid>
      <Grid size={15} sx={{ pl: 1 }}>
        <Typography data-test={rightList.titleDataTest} gutterBottom variant="h3">
          {rightList.title}
        </Typography>
        <TransferListPanel<TItem>
          ListItemComponent={ListItemComponent}
          dataTest={rightList.dataTest}
          checkboxDataTest="right-select-all"
          fields={fieldsConfig}
          hasError={!!error}
          headerButtonColor="error"
          headerButtonText="Remove Selected"
          isDisabled={isDisabled}
          isLoading={isLoading}
          items={rightList.items}
          itemsSelected={rightList.itemsSelected}
          listHeight={TRANSFER_LIST_WITH_BULK_ADD_HEIGHT}
          onBulkAdd={onBulkAdd}
          onBulkValidate={onBulkValidate}
          onSearch={rightList.onSearch}
          onSelectAll={rightList.onSelectAll}
          onSelectItem={rightList.onSelectItem}
          onTransfer={rightList.onTransfer}
          searchBarPlaceholder={rightList.searchBarPlaceholder}
          searchInputDataTest="right-search-input"
          searchPhrase={rightSearchPhrase}
          setSearchPhrase={setRightSearchPhrase}
          windowWidth={windowWidth}
        />

        {error && (
          <FormHelperText error margin="dense" sx={{ ml: 0, mt: 0.25 }}>
            {error}
          </FormHelperText>
        )}
      </Grid>

      {leftList.chipFilters && (
        <ListChipFilters<NonNullable<TFilters>>
          anchor={chipFiltersAnchor}
          filtersConfig={leftList.chipFilters.config}
          filters={leftList.chipFilters.value}
          isDisabled={isDisabled}
          onChange={leftList.chipFilters.onChange}
          setAnchor={setChipFiltersAnchor}
        />
      )}

      <ListItemFieldsFilter<TItem>
        anchor={fieldsFilterAnchor}
        fields={fieldsConfig}
        isDisabled={isDisabled}
        onClick={handleFieldFilterClick}
        onOrderChange={handleFieldFilterOrderChange}
        setAnchor={setFieldsFilterAnchor}
      />
    </Grid>
  );
};
